import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, ExternalLink, Zap, Play, Pause, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface AutomationsListProps {
  workspaceId?: string;
  baseId?: string;
}

export const AutomationsList = ({ workspaceId, baseId }: AutomationsListProps) => {
  const [automations, setAutomations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (workspaceId) {
      fetchAutomations();
    }
  }, [workspaceId]);

  const fetchAutomations = async () => {
    try {
      const { data, error } = await supabase
        .from("automations")
        .select("*, automation_runs(count)")
        .eq("workspace_id", workspaceId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAutomations(data || []);
    } catch (error: any) {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  const createAutomation = async () => {
    try {
      const { data, error } = await supabase
        .from("automations")
        .insert([{
          workspace_id: workspaceId,
          name: "New Automation",
          description: "",
          trigger_config: {},
          action_config: {},
          workflow_config: {},
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();

      if (error) throw error;
      navigate(`/automation/${data.id}`);
    } catch (error: any) {
      toast.error("Failed to create automation");
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "active":
        return {
          label: "Active",
          icon: Play,
          className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
        };
      case "paused":
        return {
          label: "Paused",
          icon: Pause,
          className: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
        };
      case "error":
        return {
          label: "Error",
          icon: AlertCircle,
          className: "bg-red-500/10 text-red-600 dark:text-red-400"
        };
      default:
        return {
          label: status || "Draft",
          icon: Pause,
          className: "bg-muted text-muted-foreground"
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading automations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Automations</h2>
          <p className="text-muted-foreground text-sm">Automate workflows with triggers and actions</p>
        </div>
        <Button onClick={createAutomation} className="gap-2">
          <Plus className="h-4 w-4" />
          New Automation
        </Button>
      </div>

      {automations.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="py-16 text-center">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Zap className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No automations yet</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
              Create automations to save time on repetitive tasks. Trigger actions based on record changes, schedules, or external events.
            </p>
            <Button onClick={createAutomation} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Automation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {automations.map((automation) => {
            const statusConfig = getStatusConfig(automation.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={automation.id} 
                className="group hover:shadow-lg hover:border-primary/30 transition-all duration-200 cursor-pointer border-border/50"
                onClick={() => navigate(`/automation/${automation.id}`)}
              >
                <CardHeader className="p-4 pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn(
                        "h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0",
                        automation.status === "active" ? "bg-emerald-500/10" : "bg-primary/10"
                      )}>
                        <Zap className={cn(
                          "h-5 w-5",
                          automation.status === "active" ? "text-emerald-600 dark:text-emerald-400" : "text-primary"
                        )} />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-base font-semibold truncate group-hover:text-primary transition-colors">
                          {automation.name}
                        </CardTitle>
                        {automation.description && (
                          <CardDescription className="text-xs truncate">
                            {automation.description}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={cn("text-xs flex-shrink-0 gap-1", statusConfig.className)}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {automation.automation_runs?.[0]?.count || 0} runs
                    </span>
                    <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
